import React, { useState, useEffect } from 'react';
import { Card, Timeline, Badge, Button, Space, Spin, Empty, Switch, message } from 'antd';
import { ClockCircleOutlined, MailOutlined, WhatsAppOutlined, BellOutlined, PauseCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import ManualReminderModal from './ManualReminderModal';

dayjs.extend(relativeTime);

/**
 * Reminder Status Card Component
 *
 * Displays reminder status and logs for a specific entity (invoice/quote/lead)
 *
 * Props:
 * - entityType: string - 'invoice', 'quote', or 'lead'
 * - entityId: string - UUID of the entity
 * - entityDisplay: string - display name (e.g., "INV-2024-001")
 */
const ReminderStatusCard = ({ entityType, entityId, entityDisplay }) => {
  const [loading, setLoading] = useState(false);
  const [reminderLogs, setReminderLogs] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [showManualModal, setShowManualModal] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (entityId) {
      fetchReminderData();
    }
  }, [entityId]);

  const fetchReminderData = async () => {
    setLoading(true);
    try {
      // Fetch reminder logs
      const logsResponse = await axios.get('/reminders/logs/by_entity/', {
        params: { entity_type: entityType, entity_id: entityId }
      });
      if (logsResponse.data) {
        setReminderLogs(logsResponse.data.results || logsResponse.data || []);
      }

      // Fetch reminder schedule
      const scheduleResponse = await axios.get('/reminders/schedules/by_entity/', {
        params: { entity_type: entityType, entity_id: entityId }
      });
      if (scheduleResponse.data) {
        setSchedule(scheduleResponse.data);
      }
    } catch (error) {
      console.error('Error fetching reminder data:', error);
    }
    setLoading(false);
  };

  const handleToggleReminders = async (enabled) => {
    if (!schedule) return;

    try {
      const endpoint = enabled
        ? `/reminders/schedules/${schedule.id}/resume/`
        : `/reminders/schedules/${schedule.id}/pause/`;

      const response = await axios.post(endpoint, {
        pause_reason: enabled ? '' : 'Manually paused by user'
      });

      if (response.status === 200) {
        messageApi.success(`Reminders ${enabled ? 'resumed' : 'paused'} successfully`);
        fetchReminderData();
      }
    } catch (error) {
      messageApi.error('Failed to update reminder status');
    }
  };

  const getChannelIcon = (channel) => {
    const icons = {
      EMAIL: <MailOutlined style={{ color: '#1890ff' }} />,
      WHATSAPP: <WhatsAppOutlined style={{ color: '#25D366' }} />,
      SMS: <BellOutlined style={{ color: '#722ed1' }} />,
    };
    return icons[channel] || <MailOutlined />;
  };

  const getStatusColor = (status) => {
    const colors = {
      SENT: 'blue',
      DELIVERED: 'green',
      READ: 'green',
      FAILED: 'red',
      PENDING: 'orange',
    };
    return colors[status] || 'default';
  };

  return (
    <>
      {contextHolder}
      <Card
        title={
          <Space>
            <ClockCircleOutlined />
            Reminder Status
          </Space>
        }
        extra={
          <Space>
            {schedule && (
              <>
                <span style={{ marginRight: 8, fontSize: '12px' }}>
                  {schedule.is_enabled ? 'Enabled' : 'Paused'}
                </span>
                <Switch
                  checked={schedule.is_enabled}
                  onChange={handleToggleReminders}
                  checkedChildren="ON"
                  unCheckedChildren="OFF"
                />
              </>
            )}
            <Button
              type="primary"
              size="small"
              onClick={() => setShowManualModal(true)}
            >
              Send Manual Reminder
            </Button>
          </Space>
        }
        style={{ marginTop: '16px' }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <Spin />
          </div>
        ) : (
          <>
            {schedule && (
              <div style={{ marginBottom: '16px', padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <div>
                    <strong>Total Reminders Sent:</strong> {schedule.total_reminders_sent}
                  </div>
                  {schedule.next_reminder_at && schedule.is_enabled && (
                    <div>
                      <strong>Next Reminder:</strong>{' '}
                      {dayjs(schedule.next_reminder_at).format('YYYY-MM-DD HH:mm')}
                      <span style={{ color: '#888', marginLeft: '8px' }}>
                        ({dayjs(schedule.next_reminder_at).fromNow()})
                      </span>
                    </div>
                  )}
                  {schedule.paused_until && !schedule.is_enabled && (
                    <div style={{ color: '#ff4d4f' }}>
                      <PauseCircleOutlined /> Paused until: {dayjs(schedule.paused_until).format('YYYY-MM-DD HH:mm')}
                    </div>
                  )}
                </Space>
              </div>
            )}

            {reminderLogs.length === 0 ? (
              <Empty description="No reminders sent yet" />
            ) : (
              <Timeline>
                {reminderLogs.map((log) => (
                  <Timeline.Item
                    key={log.id}
                    dot={getChannelIcon(log.channel)}
                    color={getStatusColor(log.status)}
                  >
                    <div>
                      <Space>
                        <strong>{dayjs(log.sent_at || log.created_at).format('MMM D, YYYY HH:mm')}</strong>
                        <Badge status={getStatusColor(log.status)} text={log.status_display} />
                      </Space>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        {log.channel_display} - {log.recipient_email || log.recipient_phone}
                      </div>
                      {log.subject && (
                        <div style={{ fontSize: '12px', color: '#888' }}>
                          Subject: {log.subject}
                        </div>
                      )}
                      {log.error_message && (
                        <div style={{ fontSize: '12px', color: '#ff4d4f', marginTop: '4px' }}>
                          Error: {log.error_message}
                        </div>
                      )}
                    </div>
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </>
        )}
      </Card>

      <ManualReminderModal
        visible={showManualModal}
        onClose={() => {
          setShowManualModal(false);
          fetchReminderData();
        }}
        entityType={entityType}
        entityId={entityId}
        entityDisplay={entityDisplay}
      />
    </>
  );
};

export default ReminderStatusCard;
